import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { Invoice } from '../../types/invoice';
import { ABOFile } from '../slices/aboSlice';
import { User } from '../slices/userSlice';

// Base selectors
const selectInvoiceState = (state: RootState) => state.invoices;
const selectABOState = (state: RootState) => state.abo;
const selectUserState = (state: RootState) => state.users;
const selectAuthState = (state: RootState) => state.auth;

// Custom equality check for arrays (Safari performance optimization)
const arrayEqualityCheck = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  
  // For small arrays, do full comparison
  if (a.length <= 100) {
    return a.every((item, index) => item === b[index]);
  }
  
  // For large arrays, sample check for Safari performance
  const sampleSize = Math.min(50, Math.floor(a.length / 10));
  const step = Math.floor(a.length / sampleSize);
  for (let i = 0; i < sampleSize; i++) {
    const index = i * step;
    if (a[index] !== b[index]) return false;
  }
  return true;
};

// Optimized invoice selectors
export const selectInvoices = createSelector(
  [selectInvoiceState],
  (invoiceState) => invoiceState.invoices,
  { memoizeOptions: { resultEqualityCheck: arrayEqualityCheck } }
);

export const selectInvoicesLoading = createSelector(
  [selectInvoiceState],
  (invoiceState) => invoiceState.loading
);

// Dashboard statistics selectors
export const selectDashboardStats = createSelector(
  [selectInvoices],
  (invoices) => {
    if (!invoices || invoices.length === 0) {
      return {
        totalInvoices: 0,
        totalAmount: 0,
        paidInvoices: 0,
        newInvoices: 0,
        pendingInvoices: 0,
        averageAmount: 0,
        paidPercentage: 0
      };
    }

    const totalInvoices = invoices.length;
    const totalAmount = invoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
    const paidInvoices = invoices.filter(invoice => invoice.status === 'paid').length;
    const newInvoices = invoices.filter(invoice => invoice.status === 'new').length;
    const pendingInvoices = invoices.filter(invoice => 
      ['pending', 'reviewed', 'ready_for_payment'].includes(invoice.status)
    ).length;
    
    const averageAmount = totalInvoices > 0 ? Math.round(totalAmount / totalInvoices) : 0;
    const paidPercentage = totalInvoices > 0 ? Math.round((paidInvoices / totalInvoices) * 100) : 0;

    return {
      totalInvoices,
      totalAmount,
      paidInvoices,
      newInvoices,
      pendingInvoices,
      averageAmount,
      paidPercentage
    };
  },
  { memoizeOptions: { resultEqualityCheck: arrayEqualityCheck } }
);

// Recent invoices selector (limited to 5 items)
export const selectRecentInvoices = createSelector(
  [selectInvoices],
  (invoices) => {
    if (!invoices || invoices.length === 0) return [];
    return invoices.slice(0, 5);
  },
  { memoizeOptions: { resultEqualityCheck: arrayEqualityCheck } }
);

// ABO selectors
export const selectABOFiles = createSelector(
  [selectABOState],
  (aboState) => aboState.aboFiles,
  { memoizeOptions: { resultEqualityCheck: arrayEqualityCheck } }
);

export const selectABOLoading = createSelector(
  [selectABOState],
  (aboState) => aboState.loading
);

// Recent ABO files selector (limited to 3 items)
export const selectRecentABOFiles = createSelector(
  [selectABOFiles],
  (aboFiles) => {
    if (!aboFiles || aboFiles.length === 0) return [];
    return aboFiles.slice(0, 3);
  },
  { memoizeOptions: { resultEqualityCheck: arrayEqualityCheck } }
);

// Users selectors
export const selectUsers = createSelector(
  [selectUserState],
  (userState) => userState.users,
  { memoizeOptions: { resultEqualityCheck: arrayEqualityCheck } }
);

export const selectUsersLoading = createSelector(
  [selectUserState],
  (userState) => userState.loading
);

// Auth selectors
export const selectCurrentUser = createSelector(
  [selectAuthState],
  (authState) => authState.user
);

// Combined loading state
export const selectDashboardLoading = createSelector(
  [selectInvoicesLoading, selectABOLoading, selectUsersLoading],
  (invoicesLoading, aboLoading, usersLoading) => 
    invoicesLoading || aboLoading || usersLoading
);

// Formatted amounts selector (memoized)
export const selectFormattedAmounts = createSelector(
  [selectDashboardStats],
  (stats) => ({
    totalAmountFormatted: stats.totalAmount.toLocaleString('cs-CZ'),
    averageAmountFormatted: stats.averageAmount.toLocaleString('cs-CZ')
  })
);
